// src/store/thunks/jewelleryPlan/jewelleryThunks.js
// ENHANCED VERSION - Adds missing thunks for complete redemption workflow

import { createAsyncThunk } from "@reduxjs/toolkit";
import reportService from "../../../services/jewelleryPlan/reportService";
import redemptionService from "../../../services/jewelleryPlan/redemptionService";
import quoteService from "../../../services/jewelleryPlan/quoteService";

// ==========================================
// Dashboard & Reports
// ==========================================

export const getDashboardStatsThunk = createAsyncThunk(
  "jewellery/getDashboardStats",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await reportService.getDashboardStats(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getDailyCollectionsThunk = createAsyncThunk(
  "jewellery/getDailyCollections",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await reportService.getDailyCollections(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getOverduesThunk = createAsyncThunk(
  "jewellery/getOverdues",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await reportService.getOverdues(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getMaturitiesThunk = createAsyncThunk(
  "jewellery/getMaturities",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await reportService.getMaturities(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const exportLedgerThunk = createAsyncThunk(
  "jewellery/exportLedger",
  async ({ planId, format }, { rejectWithValue }) => {
    try {
      const response = await reportService.exportLedger(planId, format);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==========================================
// Redemptions - EXISTING
// ==========================================

export const createRedemptionThunk = createAsyncThunk(
  "jewellery/createRedemption",
  async (redemptionData, { rejectWithValue }) => {
    try {
      const response = await redemptionService.createRedemption(redemptionData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllRedemptionsThunk = createAsyncThunk(
  "jewellery/getAllRedemptions",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getAllRedemptions(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRedemptionByIdThunk = createAsyncThunk(
  "jewellery/getRedemptionById",
  async (redemptionId, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getRedemptionById(redemptionId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getCustomerRedemptionsThunk = createAsyncThunk(
  "jewellery/getCustomerRedemptions",
  async ({ userId, filters }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getCustomerRedemptions(
        userId,
        filters
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const approveRedemptionThunk = createAsyncThunk(
  "jewellery/approveRedemption",
  async ({ redemptionId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.approveRedemption(
        redemptionId,
        approvalData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const cancelRedemptionThunk = createAsyncThunk(
  "jewellery/cancelRedemption",
  async ({ redemptionId, cancellationData }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.cancelRedemption(
        redemptionId,
        cancellationData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==========================================
// Redemptions - NEW THUNKS FOR COMPLETE WORKFLOW
// ==========================================

/**
 * Reject redemption (different from cancel - this is approval rejection)
 */
export const rejectRedemptionThunk = createAsyncThunk(
  "jewellery/rejectRedemption",
  async ({ redemptionId, reason }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.rejectRedemption(
        redemptionId,
        reason
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update payment details for redemption
 */
export const updatePaymentThunk = createAsyncThunk(
  "jewellery/updatePayment",
  async ({ redemptionId, paymentDetails }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.updatePayment(
        redemptionId,
        paymentDetails
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Complete redemption (final step in workflow)
 */
export const completeRedemptionThunk = createAsyncThunk(
  "jewellery/completeRedemption",
  async ({ redemptionId, completionData = {} }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.completeRedemption(
        redemptionId,
        completionData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get pending approvals for dashboard
 */
export const getPendingApprovalsThunk = createAsyncThunk(
  "jewellery/getPendingApprovals",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getPendingApprovals(branchId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get redemption statistics
 */
export const getRedemptionStatsThunk = createAsyncThunk(
  "jewellery/getRedemptionStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getStats(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get plan redemptions
 */
export const getPlanRedemptionsThunk = createAsyncThunk(
  "jewellery/getPlanRedemptions",
  async ({ planId, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await redemptionService.getPlanRedemptions(
        planId,
        filters
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Generate invoice for redemption
 */
export const generateInvoiceThunk = createAsyncThunk(
  "jewellery/generateInvoice",
  async (redemptionId, { rejectWithValue }) => {
    try {
      const response = await redemptionService.generateInvoice(redemptionId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==========================================
// Quote Thunks
// ==========================================

export const createQuoteThunk = createAsyncThunk(
  "jewellery/createQuote",
  async ({ planId, quoteData }, { rejectWithValue }) => {
    try {
      const response = await quoteService.createQuote({
        planId,
        ...quoteData,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getQuoteThunk = createAsyncThunk(
  "jewellery/getQuote",
  async (quoteId, { rejectWithValue }) => {
    try {
      const response = await quoteService.getQuote(quoteId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const validateQuoteThunk = createAsyncThunk(
  "jewellery/validateQuote",
  async (quoteId, { rejectWithValue }) => {
    try {
      const response = await quoteService.validateQuote(quoteId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getPlanQuotesThunk = createAsyncThunk(
  "jewellery/getPlanQuotes",
  async ({ planId, filters }, { rejectWithValue }) => {
    try {
      const response = await quoteService.getPlanQuotes(planId, filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);
